import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
  Chip,
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useFormAutoSave } from '../hooks/useFormAutoSave';
import { GrowthArea } from '../types';
import { useAuth } from 'react-oidc-context';

interface GrowthAreaModalProps {
  item: GrowthArea | null;
  onSave: (item: Omit<GrowthArea, 'id'>) => void;
  onClose: () => void;
}

const GrowthAreaModal: React.FC<GrowthAreaModalProps> = ({ item, onSave, onClose }) => {
  const { mode } = useTheme();
  const auth = useAuth();
  const preferredUsername = auth.user?.profile?.preferred_username || auth.user?.profile?.email || 'unknown-user';
  const [wordCount, setWordCount] = useState(0);

  // Initialize form data
  const initialFormData = {
    date: item ? format(new Date(item.date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
    description: item ? item.description : '',
  };

  // Use auto-save hook
  const { formData, updateFormData, clearSavedData, hasUnsavedChanges } = useFormAutoSave({
    key: item ? `growth-area-edit-${item.id}` : 'growth-area-new',
    initialData: initialFormData,
    enabled: true,
  });

  useEffect(() => {
    // Update word count when description changes
    const words = formData.description
      .trim()
      .split(/\s+/)
      .filter((word: string) => word.length > 0);
    setWordCount(words.length);
  }, [formData.description]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      alias: preferredUsername,
      date: formData.date,
      description: formData.description,
      manager_approved: false,
      manager_alias: '', // Backend will set this from PAPI
    });
    // Clear auto-saved data after successful save
    clearSavedData();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Always update the form data to allow pasting
    // Validation will prevent submission if over limit
    updateFormData({ [name]: value });
  };

  const handleClose = () => {
    // Don't clear auto-saved data on close - let user recover it later
    onClose();
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '400px',
          background:
            mode === 'dark'
              ? 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6">{item ? 'Edit Growth Area' : 'Add Growth Area'}</Typography>
          {hasUnsavedChanges && (
            <Chip icon={<SaveIcon />} label="Auto-saved" size="small" color="primary" variant="outlined" />
          )}
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              type="date"
              label="Date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
              InputLabelProps={{ shrink: true }}
            />

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Description ({wordCount}/200 words)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                placeholder="Describe the growth area or development opportunity..."
                error={wordCount > 200}
                helperText={wordCount > 200 ? 'Description exceeds 200 word limit' : ''}
              />
            </Box>

            {wordCount > 200 && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Description exceeds 200 word limit
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={onClose} variant="outlined" color="secondary">
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={wordCount > 200}>
            {item ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default GrowthAreaModal;
